import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert, Badge, Button, Code, Divider, Drawer, Group, Skeleton, Stack, Text,
  Textarea, TextInput, Tooltip, UnstyledButton,
} from '@mantine/core'
import { IconAlertTriangle, IconInfoCircle, IconRefresh, IconRestore } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import {
  channelLabel, notificationTemplatesApi, sourceLabel,
  type NotificationTemplateVariable,
} from '@/api/notification-templates'
import { formatApiError } from '@/api/errors'

// ─── Mensagens de erro do backend (§7) em PT-BR ───────────────────────────────
// O `detail` vem como código, às vezes com sufixo `:<nome do campo/variável>`.

const ERROR_MESSAGES: Record<string, string> = {
  empty_body: 'O corpo da mensagem não pode ficar vazio.',
  empty_subject: 'O assunto não pode ficar vazio.',
  markup_not_supported:
    'Este canal não renderiza formatação — remova a marcação (** ou _) do texto.',
  invalid_markup:
    'Depois da limpeza de HTML o corpo ficou vazio. Revise a marcação usada.',
  template_not_editable: 'Esta mensagem ainda não é editável pela tela.',
  unknown_template: 'Esta mensagem não existe no catálogo.',
}

const FIELD_LABELS: Record<string, string> = {
  body: 'corpo',
  subject: 'assunto',
}

/** Traduz o `detail` da API, inclusive os códigos com sufixo (`unknown_variable:x`). */
function formatTemplateError(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (typeof detail !== 'string') return formatApiError(err, fallback)

  if (ERROR_MESSAGES[detail]) return ERROR_MESSAGES[detail]

  const [code, arg] = detail.split(':', 2)
  if (code === 'unknown_variable' && arg) {
    return `Variável desconhecida: {{${arg}}}. Use apenas as variáveis da lista ao lado.`
  }
  if (code === 'missing_required_variable' && arg) {
    return `A variável obrigatória {{${arg}}} não pode ser removida do texto.`
  }
  if (code === 'too_long' && arg) {
    return `Texto acima do tamanho permitido (campo: ${FIELD_LABELS[arg] ?? arg}).`
  }
  return formatApiError(err, fallback)
}

// ─── Validação local (espelha §7 para não deixar o Admin salvar em erro) ──────

/** Limites por canal — iguais aos do backend. */
const LIMITS: Record<string, { subject: number; body: number }> = {
  // 200 é o limite do backend (spec §7) — divergir aqui deixaria o Salvar
  // passar e o servidor recusar com too_long:subject.
  email: { subject: 200, body: 20_000 },
  feed: { subject: 120, body: 300 },
  chat: { subject: 120, body: 2_000 },
}

const PLACEHOLDER_RE = /\{\{\s*([A-Za-z0-9_.]+)\s*\}\}/g

/** Nomes de variáveis usados no texto, na ordem em que aparecem (sem repetir). */
export function extractPlaceholders(text: string): string[] {
  const found: string[] = []
  for (const match of text.matchAll(PLACEHOLDER_RE)) {
    const name = match[1]
    if (!found.includes(name)) found.push(name)
  }
  return found
}

/**
 * Erros que bloqueiam o Salvar, calculados no cliente.
 *
 * O servidor continua sendo a autoridade — isto existe só para o Admin
 * descobrir o problema antes de perder o clique, com o nome errado à mão.
 */
export function validarTexto(
  { channel, subject, body, variables }: {
    channel: string
    subject: string
    body: string
    variables: NotificationTemplateVariable[]
  },
): string[] {
  const erros: string[] = []
  const permitidas = variables.map(v => v.name)
  const limites = LIMITS[channel] ?? LIMITS.feed

  if (!body.trim()) erros.push('O corpo da mensagem não pode ficar vazio.')
  if (channel === 'email' && !subject.trim()) erros.push('O assunto não pode ficar vazio.')

  const usadas = extractPlaceholders(`${subject}\n${body}`)
  for (const nome of usadas) {
    if (!permitidas.includes(nome)) {
      erros.push(
        `Variável desconhecida: {{${nome}}}. Use apenas as variáveis da lista ao lado.`,
      )
    }
  }
  for (const v of variables) {
    if (v.required && !usadas.includes(v.name)) {
      erros.push(`A variável obrigatória {{${v.name}}} não pode ser removida do texto.`)
    }
  }

  if (body.length > limites.body) {
    erros.push(`O corpo passou do limite de ${limites.body} caracteres.`)
  }
  if (subject.length > limites.subject) {
    erros.push(`O assunto passou do limite de ${limites.subject} caracteres.`)
  }

  // Feed é texto puro no v1 (§6.3): aceitar `**` em silêncio mostraria
  // asteriscos literais ao usuário final.
  if (channel === 'feed') {
    const semVariaveis = body.replace(PLACEHOLDER_RE, '')
    if (semVariaveis.includes('**')) {
      erros.push('O canal Feed não renderiza formatação — remova os ** do texto.')
    }
  }

  return erros
}

// ─── Componente ───────────────────────────────────────────────────────────────

interface MensagemEditorDrawerProps {
  eventKey: string | null
  channel: string | null
  /** `null` = escopo de plataforma; número = override daquela Organização. */
  orgId?: number | null
  /** Só para a redação da tela ("os textos de Acme"). */
  orgName?: string | null
  opened: boolean
  onClose: () => void
  /** Vêm da listagem: o detalhe da API não devolve autoria do override. */
  updatedByEmail?: string | null
  updatedAt?: string | null
}

/**
 * Editor de um texto do catálogo (spec 2026-07-25 §9).
 *
 * O Admin edita **conteúdo**: o shell da marca, o cabeçalho e o rodapé do
 * e-mail seguem vindo do código. A prévia roda em `<iframe sandbox="">` —
 * sem `allow-scripts` — como defesa em profundidade: mesmo uma falha do
 * sanitizador do servidor não executa nada no navegador de quem edita.
 */
export function MensagemEditorDrawer({
  eventKey, channel, orgId = null, orgName = null, opened, onClose,
  updatedByEmail, updatedAt,
}: MensagemEditorDrawerProps) {
  const qc = useQueryClient()
  const bodyRef = useRef<HTMLTextAreaElement>(null)

  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [erroApi, setErroApi] = useState<string | null>(null)

  const habilitado = opened && !!eventKey && !!channel
  const escopoOrg = orgId != null

  const { data: detail, isLoading } = useQuery({
    queryKey: ['notification-template', eventKey, channel, orgId],
    queryFn: () => notificationTemplatesApi.get(eventKey as string, channel as string, orgId),
    enabled: habilitado,
    refetchOnWindowFocus: false,
  })

  const preview = useMutation({
    mutationFn: (input: { subject: string | null; body: string }) =>
      notificationTemplatesApi.preview(eventKey as string, channel as string, input, orgId),
  })

  // Carrega o texto efetivo no formulário. Depois de salvar/restaurar o
  // refetch traz o novo valor e o formulário acompanha — é o comportamento
  // desejado (o que está na tela é o que está salvo).
  useEffect(() => {
    if (!detail) return
    setSubject(detail.subject ?? '')
    setBody(detail.body ?? '')
    setErroApi(null)
    if (detail.editable) {
      // Chat: o assunto não vai nem para a prévia — o canal não tem assunto.
      preview.mutate({
        subject: channel === 'chat' ? null : detail.subject,
        body: detail.body,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail])

  const variables = detail?.variables ?? []
  const canal = channel ?? ''
  // Chat não tem assunto: o `render` descarta o `subject` e a validação não o
  // exige — o campo na tela só prometia um campo que ninguém lê (spec §4).
  const canalSemAssunto = canal === 'chat'
  const erros = useMemo(
    () => (detail ? validarTexto({ channel: canal, subject, body, variables }) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [detail, canal, subject, body],
  )

  const salvar = useMutation({
    mutationFn: () =>
      notificationTemplatesApi.save(eventKey as string, channel as string, {
        // Chat não tem assunto: o campo nem aparece, e mandar o valor herdado
        // do detalhe gravaria lixo que o servidor descarta.
        subject: canalSemAssunto ? null : (subject.trim() ? subject : null),
        body,
      }, orgId),
    onSuccess: () => {
      setErroApi(null)
      qc.invalidateQueries({ queryKey: ['notification-templates'] })
      qc.invalidateQueries({ queryKey: ['notification-template', eventKey, channel] })
      notifications.show({
        title: 'Mensagem salva',
        message: escopoOrg
          ? `O texto passa a valer nos próximos envios para os membros de ${orgName ?? 'sua organização'}.`
          : 'O texto personalizado passa a valer nos próximos envios.',
        color: 'green',
      })
    },
    onError: (err) => setErroApi(formatTemplateError(err, 'Falha ao salvar a mensagem')),
  })

  const restaurar = useMutation({
    mutationFn: () =>
      notificationTemplatesApi.reset(eventKey as string, channel as string, orgId),
    onSuccess: () => {
      setErroApi(null)
      qc.invalidateQueries({ queryKey: ['notification-templates'] })
      qc.invalidateQueries({ queryKey: ['notification-template', eventKey, channel] })
      notifications.show({
        title: escopoOrg ? 'Texto da plataforma restaurado' : 'Padrão restaurado',
        message: escopoOrg
          ? 'A mensagem volta a seguir o texto da plataforma.'
          : 'A mensagem volta a seguir o texto que vem no código.',
        color: 'blue',
      })
    },
    onError: (err) => setErroApi(formatTemplateError(
      err,
      escopoOrg ? 'Falha ao restaurar o texto da plataforma' : 'Falha ao restaurar o padrão',
    )),
  })

  // No escopo de org o DELETE apaga só o texto DESTA org: a mensagem volta ao
  // texto da plataforma, não ao padrão do código (spec 2026-07-27 §2.4). Dizer
  // "restaurar padrão" aqui prometeria o que o botão não faz.
  const rotuloRestaurar = escopoOrg ? 'Restaurar texto da plataforma' : 'Restaurar padrão'

  const confirmarRestauracao = () => {
    modals.openConfirmModal({
      title: escopoOrg ? 'Voltar ao texto da plataforma?' : 'Restaurar o texto padrão?',
      children: escopoOrg ? (
        <Text size="sm">
          O texto desta Organização é apagado e a mensagem volta a seguir o
          <b> texto da plataforma</b> — e, se a plataforma também não tiver
          personalizado, o padrão do sistema. Não há como desfazer.
        </Text>
      ) : (
        <Text size="sm">
          O texto personalizado é apagado e a mensagem volta a seguir o padrão que
          vem no código — inclusive as correções futuras. Não há como desfazer.
        </Text>
      ),
      labels: { confirm: rotuloRestaurar, cancel: 'Cancelar' },
      confirmProps: { color: 'red' },
      onConfirm: () => restaurar.mutate(),
    })
  }

  /** Insere `{{nome}}` na posição do cursor do corpo (ou no fim, se não houver foco). */
  const inserirVariavel = (nome: string) => {
    const token = `{{${nome}}}`
    const el = bodyRef.current
    if (!el) {
      setBody(prev => prev + token)
      return
    }
    const inicio = el.selectionStart ?? body.length
    const fim = el.selectionEnd ?? inicio
    const novo = body.slice(0, inicio) + token + body.slice(fim)
    setBody(novo)
    requestAnimationFrame(() => {
      el.focus()
      const pos = inicio + token.length
      el.setSelectionRange(pos, pos)
    })
  }

  const fechar = () => {
    setErroApi(null)
    onClose()
  }

  const assuntoLabel = canal === 'email' ? 'Assunto' : 'Título'
  const avisos = [...(detail?.warnings ?? []), ...(preview.data?.warnings ?? [])]
  const previewHtml = preview.data?.body_html ?? ''

  // A origem só é informativa no escopo de org — na plataforma o selo
  // "personalizado" já diz tudo o que há para dizer.
  const origemLabel = escopoOrg ? sourceLabel(detail?.source) : null

  // Só há o que apagar quando o override é DESTA org; se o texto vem da
  // plataforma, o DELETE por org não teria efeito (ou responderia 404).
  const podeRestaurar = escopoOrg
    ? (detail?.source ? detail.source === 'org' : !!detail?.is_customized)
    : !!detail?.is_customized

  return (
    <Drawer
      opened={opened}
      onClose={fechar}
      position="right"
      size="xl"
      title={
        <Group gap="xs">
          <Text fw={600}>{detail?.label ?? 'Mensagem do sistema'}</Text>
          {channel && <Badge size="sm" variant="light">{channelLabel(channel)}</Badge>}
          {escopoOrg && orgName && (
            <Badge size="sm" color="blue" variant="light">{orgName}</Badge>
          )}
          {detail?.is_customized && !escopoOrg && (
            <Badge size="sm" color="orange" variant="light">personalizado</Badge>
          )}
        </Group>
      }
    >
      {isLoading && (
        <Stack gap="sm">
          <Skeleton height={28} />
          <Skeleton height={180} />
        </Stack>
      )}

      {detail && !detail.editable && (
        <Alert color="gray" variant="light" icon={<IconInfoCircle size={16} />}>
          Esta mensagem ainda é montada no código — a edição pela tela chega em breve.
        </Alert>
      )}

      {detail && detail.editable && (
        <Stack gap="md">
          <Text size="sm" c="dimmed">{detail.description}</Text>
          <Text size="xs" c="dimmed">Quem recebe: {detail.recipients_note}</Text>

          {canal === 'feed' && (
            <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
              Texto simples: este canal não renderiza formatação. Negrito e itálico
              apareceriam como asteriscos para quem recebe.
            </Alert>
          )}

          <Group align="flex-start" gap="lg" wrap="nowrap">
            <Stack gap="sm" style={{ flex: 2, minWidth: 0 }}>
              {!canalSemAssunto && (
                <TextInput
                  label={assuntoLabel}
                  value={subject}
                  onChange={(e) => setSubject(e.currentTarget.value)}
                  withAsterisk={canal === 'email'}
                />
              )}
              <Textarea
                label="Corpo da mensagem"
                description={canal === 'email'
                  ? 'HTML interno do e-mail — o cabeçalho, o rodapé e a identidade visual vêm do sistema.'
                  : 'Uma linha, em texto puro.'}
                ref={bodyRef}
                value={body}
                onChange={(e) => setBody(e.currentTarget.value)}
                autosize
                minRows={8}
                maxRows={24}
                styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
              />
            </Stack>

            <Stack gap="xs" style={{ flex: 1, minWidth: 180 }}>
              <Text size="sm" fw={600}>Variáveis</Text>
              <Text size="xs" c="dimmed">Clique para inserir no corpo.</Text>
              {variables.length === 0 && (
                <Text size="xs" c="dimmed">Esta mensagem não usa variáveis.</Text>
              )}
              {variables.map(v => (
                <Tooltip key={v.name} label={v.description} multiline w={220} withArrow>
                  <UnstyledButton
                    aria-label={`Inserir variável {{${v.name}}}`}
                    onClick={() => inserirVariavel(v.name)}
                  >
                    <Group gap={6} wrap="nowrap">
                      <Code>{`{{${v.name}}}`}</Code>
                      {v.required && (
                        <Badge size="xs" color="red" variant="light">obrigatória</Badge>
                      )}
                    </Group>
                  </UnstyledButton>
                </Tooltip>
              ))}
            </Stack>
          </Group>

          {erros.length > 0 && (
            <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}
              title="Corrija antes de salvar">
              <Stack gap={2}>
                {erros.map(e => <Text key={e} size="sm">{e}</Text>)}
              </Stack>
            </Alert>
          )}

          {erroApi && (
            <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
              {erroApi}
            </Alert>
          )}

          {avisos.length > 0 && (
            <Alert color="yellow" variant="light" icon={<IconInfoCircle size={16} />}
              title="Avisos de compatibilidade">
              <Stack gap={2}>
                {avisos.map(a => <Text key={a} size="sm">{a}</Text>)}
              </Stack>
            </Alert>
          )}

          <Divider
            label={
              <Group gap="xs">
                <Text size="xs">Prévia</Text>
                <Button
                  size="compact-xs"
                  variant="subtle"
                  leftSection={<IconRefresh size={12} />}
                  loading={preview.isPending}
                  onClick={() => preview.mutate({
                    subject: canalSemAssunto ? null : (subject.trim() ? subject : null),
                    body,
                  })}
                >
                  Atualizar prévia
                </Button>
              </Group>
            }
            labelPosition="left"
          />

          {!canalSemAssunto && preview.data?.subject && (
            <Text size="sm"><b>{assuntoLabel}:</b> {preview.data.subject}</Text>
          )}
          {/* sandbox="" (sem allow-scripts) é requisito de segurança, não estética:
              a SPA nunca injeta HTML vindo do banco — nem com o sanitizador do
              servidor no caminho. */}
          <iframe
            title="Prévia da mensagem"
            sandbox=""
            srcDoc={previewHtml}
            style={{
              width: '100%',
              height: 320,
              border: '1px solid var(--ai-border-light)',
              borderRadius: 4,
              background: 'var(--ai-card-bg)',
            }}
          />

          {/* De onde vem o texto que está na tela. Sem isto o gestor vê um
              texto diferente do padrão sem ter mexido em nada e não entende
              por quê — a cascata é org → plataforma → padrão (§2.4). */}
          {origemLabel && (
            <Text size="xs" c="dimmed">Origem do texto vigente: {origemLabel}.</Text>
          )}

          {updatedByEmail && updatedAt && (
            <Text size="xs" c="dimmed">
              Última alteração por {updatedByEmail} em {dayjs(updatedAt).format('DD/MM [às] HH:mm')}
            </Text>
          )}

          <Group justify="space-between">
            <Button
              onClick={() => salvar.mutate()}
              loading={salvar.isPending}
              disabled={erros.length > 0}
            >
              Salvar
            </Button>
            {podeRestaurar && (
              <Button
                variant="subtle"
                color="red"
                leftSection={<IconRestore size={16} />}
                loading={restaurar.isPending}
                onClick={confirmarRestauracao}
              >
                {rotuloRestaurar}
              </Button>
            )}
          </Group>
        </Stack>
      )}
    </Drawer>
  )
}
